import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

// ModalShell — Polish B2 mobile/a11y tranche. Reusable centered-card modal: focus trap (incl. Tab cycling),
// Escape closes, scrim click closes, body scroll lock, safe-area padding, role=dialog aria-modal, initial focus
// on mount, returns focus to the trigger on close. Ported from PSCraft v2's workspace/mobile/MobileModalShell.tsx
// pattern (focus-trap-react + Tailwind) into CVCraft's own idiom: a hand-rolled trap (CVCraft has no
// focus-trap-react dependency) + inline CSSProperties (no tailwind here).
//
// No responsive bottom-sheet branch — CVCraft's modals (PaywallModal) are small enough that a centered card at
// every width is fine; Drawer.tsx below is the mobile-specific pattern (the tailoring rail).

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ModalShell({
  onClose, label, children, testId = 'modal-shell', dismissable = true,
}: {
  onClose: () => void;
  label: string;
  children: ReactNode;
  testId?: string;
  // If false, neither Escape nor a scrim click dismisses — used where dismissal must be an explicit in-content action.
  dismissable?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Initial focus + return-focus-on-close.
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();
    return () => { previouslyFocused.current?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape + Tab-cycling focus trap.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { if (dismissable) onClose(); return; }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) { e.preventDefault(); last.focus(); }
      } else {
        if (active === last || !panel.contains(active)) { e.preventDefault(); first.focus(); }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, dismissable]);

  // Body scroll lock.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      style={S.scrim}
      data-testid={`${testId}-scrim`}
      onClick={() => dismissable && onClose()}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        style={S.panel}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  scrim: {
    position: 'fixed', inset: 0, background: 'rgba(16,42,67,0.45)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '16px calc(env(safe-area-inset-right) + 16px) calc(env(safe-area-inset-bottom) + 16px) calc(env(safe-area-inset-left) + 16px)',
    zIndex: 50,
  },
  panel: {
    background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 10, boxShadow: 'var(--elev-overlay)', padding: 'var(--space-5) var(--space-5)',
    maxWidth: 440, width: '100%', maxHeight: '90vh', overflowY: 'auto', fontFamily: 'var(--sans)', outline: 'none',
  },
};
